import { useState, useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import Loader from 'components/Loader';
import { getIsSuper, has_permission } from 'config/permissions';
import { DetailHeader } from 'components/Forms/DetailHeader';
import { TabAccordion } from 'components/Forms/TabAccordion';
import { adminTypes } from './constants';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import { showToast } from 'components/Toast';
import { getAdminsState, getActiveRole } from 'selectors/admins';
import { AdminAccordion } from './AdminAccordion';
import {
  useCreateAdminMutation,
  usePatchAdminUserAccessMutation,
  useGetAdminUserDetailQuery,
} from 'services/adminsApi';
import { adminCreatedSuccess } from 'store/admins/slice';
import { getRecipients } from 'store/recipients/actions';
import { getPartners } from 'store/partners/actions';
import { FORM_ERROR } from 'final-form';
import { RecipientsRequestType } from 'store/recipients/types';
import { datadogLogs } from '@datadog/browser-logs';
import { trackEvent } from 'helpers/mixpanelEvents';

export const AdminsDetail = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [itemVisible, setItemVisible] = useState(0);
  const [tabStatus, setTabStatus] = useState(['in-progress']);
  const [currentRoleType, setCurrentRoleType] = useState('super_admin');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const activeRole = useAppSelector(getActiveRole);
  const [createAdminMutation, { isLoading: isCreateLoading }] = useCreateAdminMutation();
  const [patchAdminUserAccessMutation, { isLoading: isPatchLoading }] =
    usePatchAdminUserAccessMutation();

  // Determine mode via URL params
  const urlParams = new URLSearchParams(location.search);
  const adminId = urlParams.get('admin_id');
  const isEditMode = !!adminId;

  const adminsState = useAppSelector(state => getAdminsState(state));

  // Fetch admin data when in edit mode using RTK Query
  const {
    data: adminDetail,
    isLoading: isAdminDetailLoading,
    isFetching: isAdminDetailFetching,
  } = useGetAdminUserDetailQuery(
    { adminId: adminId || '' },
    {
      skip: !isEditMode || !adminId || !activeRole,
    }
  );

  // Track form values to detect role type changes
  const [formValues, setFormValues] = useState<any>(null);

  // Load recipients data when recipient_admin is selected
  useEffect(() => {
    if (currentRoleType === 'recipient_admin') {
      dispatch(
        getRecipients({ payment_processor: 'any', request_type: RecipientsRequestType.admin })
      ); // Load all recipients
    }
  }, [currentRoleType, dispatch]);

  // Load partners data when partner_admin is selected
  useEffect(() => {
    if (currentRoleType === 'partner_admin') {
      dispatch(getPartners({ search_str: '', partner_ids: [] }, activeRole)); // Load all partners
    }
  }, [currentRoleType, dispatch, activeRole]);

  // Load initial data if editing an admin with recipient_admin or partner_admin role
  useEffect(() => {
    if (isEditMode && adminDetail) {
      const roleType = adminDetail.role_type;
      if (roleType === 'recipient_admin') {
        dispatch(
          getRecipients({ payment_processor: 'any', request_type: RecipientsRequestType.admin })
        );
      } else if (roleType === 'partner_admin') {
        dispatch(getPartners({ search_str: '', partner_ids: [] }, activeRole));
      }
      setCurrentRoleType(roleType);
    }
  }, [isEditMode, adminDetail, dispatch]);

  // Track form role type changes
  useEffect(() => {
    if (formValues?.role_type && formValues.role_type !== currentRoleType) {
      setCurrentRoleType(formValues.role_type);
    }
  }, [formValues?.role_type, currentRoleType]);

  const initialValues = useMemo(() => {
    if (isEditMode && adminDetail) {
      return {
        first_name: adminDetail.first_name,
        last_name: adminDetail.last_name,
        email: adminDetail.email || '',
        role_type: formValues?.role_type,
        subscription_level: 'BASIC',
        is_primary_contact: false,
        other_position_category: null,
        partners: [],
        recipients: [],
        partnersSelected: [],
        recipientsSelected: [],
      };
    }

    // Set default values for create mode
    return {
      first_name: '',
      last_name: '',
      email: '',
      role_type: 'super_admin',
      subscription_level: 'BASIC',
      is_primary_contact: false,
      partners: [],
      recipients: [],
      partnersSelected: [],
      recipientsSelected: [],
    };
  }, [isEditMode, adminDetail]);

  const saveForm = async (values: any) => {
    if (!getIsSuper(activeRole)) {
      return; // Do nothing if not super admin
    }

    if (isEditMode && adminId) {
      setIsSubmitting(true);
      const body = {
        admin_user_id: adminId,
        active_role: activeRole,
        email: values.email,
        first_name: values.first_name,
        last_name: values.last_name,
        role_type: values.role_type,
        existing_role_types: adminDetail?.role_names || [],
        is_primary_contact: values.primary_contact || false,
        partners: values?.partnersSelected?.map((item: any) => item.value) || [],
        recipients: values?.recipientsSelected?.map((item: any) => item.value) || [],
      };
      try {
        const response = await patchAdminUserAccessMutation(body).unwrap();
        trackEvent('Admin Saved', {
          screen: 'admin_detail',
          interaction_type: 'edit',
          role_type: values.role_type,
        });
        setIsSubmitting(false);
        dispatch(adminCreatedSuccess(response));
        const defaultSuccessMessage = 'Success!';
        let customSuccessMessage = 'Your admin has been updated';
        if (response.update_message) {
          customSuccessMessage = `This user's new role will take effect when the nightly script next runs`;
        }
        const fullSuccessMessage = `${defaultSuccessMessage} ${customSuccessMessage}`;
        showToast(fullSuccessMessage, 'success');
        navigate('/admins');
        return response;
      } catch (error: any) {
        setIsSubmitting(false);
        datadogLogs.logger.error('Error occurred patching admin user access:', {}, error as Error);
        const errorMessage =
          error?.data?.message || error?.data || error?.message || 'An error occurred';
        const errorStatus = error?.status || error?.response?.status;
        showToast(errorMessage, 'error', errorStatus);
        // Return error object to reset form's submitting state
        return { [FORM_ERROR]: errorMessage };
      }
    }

    let position_category = values.position_category ? values.position_category.trim() : '';
    const other_position_category = values.other_position_category
      ? values.other_position_category.trim()
      : '';

    if (
      (position_category === 'OTHER' || position_category === '') &&
      other_position_category !== ''
    ) {
      position_category = other_position_category;
    }

    setIsSubmitting(true);
    const body = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      role_type: values.role_type,
      subscription_level: values.subscription_level,
      phone_number:
        values.phone_number && !values.phone_number.startsWith('+')
          ? `+${values.phone_number}`
          : values.phone_number || null,
      position_category: position_category !== '' ? position_category : null,
      is_primary_contact: values.primary_contact || false,
      partners:
        values?.partnersSelected?.map((item: any) => ({
          id: item.value,
          name: item.label,
        })) || [],
      recipients:
        values?.recipientsSelected?.map((item: any) => ({
          id: item.value,
          name: item.label,
        })) || [],
    };
    try {
      const response = await createAdminMutation({ body }).unwrap();
      trackEvent('Admin Saved', {
        screen: 'admin_detail',
        interaction_type: 'create',
        role_type: values.role_type,
      });
      setIsSubmitting(false);
      dispatch(adminCreatedSuccess(response));
      showToast(`Success! Your admin has been ${isEditMode ? 'updated' : 'created'}`, 'success');
      navigate('/admins');
      return response;
    } catch (error: any) {
      setIsSubmitting(false);
      console.log(error);
      const errorMessage =
        error?.data?.message || error?.data || error?.message || 'An error occurred';
      const errorStatus = error?.status || error?.response?.status;
      showToast(errorMessage, 'error', errorStatus);
      return {
        [FORM_ERROR]: errorMessage,
      };
    }
  };

  const updateStatus = (item: number, status: string) => {
    setTabStatus(prevStatus =>
      prevStatus.map((s: string, index: number) => (index === item ? status : s))
    );
  };

  const handleClickAccordion = (item: number, form: any) => {
    setItemVisible(prev => (prev === item ? -1 : item));

    if (tabStatus[item] === '') {
      updateStatus(item, 'in-progress');
    } else {
      const status = form.getState().invalid ? 'error' : 'success';
      updateStatus(item, status);
    }
  };

  return (
    <>
      {(adminsState.isLoading ||
        (isEditMode && (isAdminDetailLoading || isAdminDetailFetching))) && <Loader type="dots" />}
      <Form
        onSubmit={saveForm}
        initialValues={initialValues}
        subscription={{ values: true }}
        render={({ handleSubmit, form, values }) => {
          if (values !== formValues) {
            setTimeout(() => setFormValues(values), 0);
          }

          return (
            <>
              {!has_permission('ADMINS_DETAIL_ROUTE', 'R', activeRole) ? (
                <div className="content">
                  <h1 className="title-permission">
                    Sorry, you don&apos;t have permission to see this screen.
                  </h1>
                </div>
              ) : (
                <>
                  <DetailHeader
                    saveForm={handleSubmit}
                    type="Admin"
                    value={isEditMode ? 'Update' : 'Create'}
                    isNew={!isEditMode}
                    backTo="/admins"
                  />
                  <div className="content-full-width">
                    <form onSubmit={handleSubmit}>
                      {adminTypes.admin.map((type, index) => (
                        <div key={index}>
                          <TabAccordion
                            index={index}
                            itemVisible={itemVisible}
                            type={type}
                            tabStatus={tabStatus}
                            handleClickAccordion={() => handleClickAccordion(index, form)}
                          />
                          <AdminAccordion
                            index={index}
                            itemVisible={itemVisible}
                            formState={form}
                            isLoading={
                              isEditMode
                                ? isSubmitting || isPatchLoading
                                : isSubmitting || isCreateLoading || adminsState.isLoading
                            }
                            isEditMode={isEditMode}
                          />
                        </div>
                      ))}
                    </form>
                  </div>
                </>
              )}
            </>
          );
        }}
      />
    </>
  );
};
